import React, { memo, useMemo, useState } from 'react';
import { LineItemForm, PriceSectionForm } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { Formatter } from '@core/utils/formatter';
import { TableColumn } from '@modules/common/TableColumn';
import { FormCheckbox, FormInput } from '@core/components/form';
import { R } from '@core/utils/r';
import { CardTitle, Col, Collapse, Row } from 'reactstrap';
import { ProposalService } from '@business/services';
import { LabourTypeResponse } from '@business/entities/labour-type';
import { Styles } from '@core/utils/css';
import { useCompany } from '@core/utils/hooks/misc';
import { useProposalContext } from '@modules/proposal/ProposalBuilderPage';
import { Config } from '@config';

export const PricingHeader = memo((props: { isLaborActive: boolean; editable?: boolean; primaryColor?: string }) => {
  return (
    <div className="proposal-table-header" style={{ color: props.primaryColor }}>
      <TableColumn width={50} maxWidth={50}></TableColumn>
      <TableColumn>Description</TableColumn>
      <TableColumn maxWidth={100}>Qty</TableColumn>
      <TableColumn maxWidth={100}>Rate</TableColumn>
      {props.editable && props.isLaborActive && (
        <>
          <TableColumn maxWidth={100}>Phase</TableColumn>
          <TableColumn maxWidth={100}>Labor Hours</TableColumn>
          <TableColumn maxWidth={100}>Labor Price</TableColumn>
        </>
      )}
      <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
        Subtotal
      </TableColumn>
      <TableColumn maxWidth={5}></TableColumn>
    </div>
  );
});

export interface PricingFooterProps {
  total: number;
  primaryColor?: string;
}
export const PricingFooter = memo((props: PricingFooterProps) => {
  const { total } = props;
  const company = useCompany();

  return (
    <div className="proposal-table-footer d-flex">
      <TableColumn></TableColumn>
      <TableColumn maxWidth={200} width={200} style={{ fontWeight: 'bold', marginLeft: 20 }}>
        {Formatter.formatCurrency(total, company.currencyCode)}
      </TableColumn>
    </div>
  );
});

export interface LineItemProps {
  form: LineItemForm;
  index: number;
  canSign: boolean;
  labourTypes: LabourTypeResponse[];
  isLaborActive: boolean;
  isPackage?: boolean;
  editable?: boolean;
  onOpen?: () => void;
  isOpen?: boolean;
  canCheckProducts?: boolean;
}

export const LineItem = memo((props: LineItemProps) => {
  const {
    form,
    index,
    canSign,
    isLaborActive,
    labourTypes = [],
    isPackage = false,
    editable = true,
    isOpen = false,
    canCheckProducts = true,
  } = props;
  const formValue = useNonNilObservable(form.value$);
  const company = useCompany();
  const { form: proposalForm } = useProposalContext();
  const proposalFormValues = useNonNilObservable(proposalForm.value$);
  const combineLabor = useMemo(() => proposalFormValues.combineLabor, [proposalFormValues]);

  const subtotal = useMemo(() => {
    // if (R.includes(formValue.service, 'package')) {
    //   return formValue.rate + formValue.labourPrice;
    // }

    return ProposalService.getSubTotalNew(formValue, true, editable, combineLabor);

    return ProposalService.getSubTotal(formValue, true, editable);
  }, [editable, formValue, combineLabor]);

  const lineItemRate = useMemo(() => {
    return Formatter.formatCurrency(formValue.rate, company.currencyCode);
  }, [company, formValue.rate]);

  const phaseName = useMemo(() => {
    if (formValue.labourType == '0') {
      return 'Multiple';
    } else {
      return R.find(labourTypes, item => item.id == formValue.labourType)?.name;
      //   const laborItems = formValue.productLabourItems.length ? formValue.productLabourItems[0] : formValue.labourItems;
    }
    // return formValue.labourType ;
  }, [formValue.labourType, labourTypes]);

  const labourHour = useMemo(() => {
    if (isPackage && formValue.packageItems.length > 0) {
      return ProposalService.getTotalLaborHours(formValue);
    }
    if (formValue.labourType == '0') {
      return formValue.labour;
    } else {
      if (formValue.labourItems.length) return formValue.labourItems[0].hour;
    }
  }, [formValue, isPackage]);

  const labourPrice = useMemo(() => {
    if (isPackage && formValue.packageItems.length > 0) {
      return ProposalService.getTotalLaborPrice(formValue);
    } else {
      if (formValue.labourType == '0') {
        return formValue.labourPrice;
      } else {
        if (formValue.labourItems.length) return formValue.labourItems[0].price;
      }
    }
  }, [isPackage, formValue]);

  const rate = useMemo(() => {
    if (isPackage && formValue.packageItems.length > 0) {
      return ProposalService.getTotalRate(formValue);
    } else {
      return formValue.rate;
    }
  }, [isPackage, formValue]);

  const totalQty = useMemo(() => {
    if (isPackage && formValue.packageItems.length > 0) {
      return ProposalService.getTotalQty(formValue);
    } else {
      return formValue.qty;
    }
  }, [formValue, isPackage]);

  const currentItemTypePackage = useMemo(() => {
    return formValue.packageItems.length > 0 ? true : false;
  }, [formValue.packageItems.length]);

  return (
    <div className="d-flex align-items-center" style={{ padding: '10px 0px' }}>
      <TableColumn width={50} maxWidth={50}>
        {formValue.isOptional && <FormCheckbox disabled={!canCheckProducts} style={{ lineHeight: 1 }} noPadding control={form.controls.selected} />}
      </TableColumn>
      {/* {!isPackage && !editable && ( */}
      {!editable && !R.isEmpty(formValue.productImage) && (
        <TableColumn maxWidth={60}>
          <img
            src={R.isEmpty(formValue.productImage) ? Styles.assets('images/default_product.png') : (formValue.productImage as string)}
            style={{ width: '100%' }}
            className="img-fluid"
          />
        </TableColumn>
      )}
      {/* )} */}
      <TableColumn>{formValue.description}</TableColumn>
      <TableColumn maxWidth={100}>
        {!currentItemTypePackage &&
          (formValue.flexibleQty && !!!canSign ? <FormInput withoutGroup control={form.controls.qty} type="number" /> : totalQty)}
        {/* {isPackage ? totalQty : formValue.flexibleQty && !!!canSign ? <FormInput withoutGroup control={form.controls.qty} type="number" /> : totalQty} */}
      </TableColumn>
      <TableColumn maxWidth={100}>{!currentItemTypePackage && Formatter.formatCurrency(rate, company.currencyCode)}</TableColumn>
      {editable && isLaborActive && (
        <>
          <TableColumn maxWidth={100}>{phaseName}</TableColumn>
          <TableColumn maxWidth={100}>{R.round(labourHour || 0, 2)}</TableColumn>
          <TableColumn maxWidth={100}>{Formatter.formatCurrency(labourPrice, company.currencyCode)}</TableColumn>
        </>
      )}

      {/* <TableColumn maxWidth={125}>{lineItemRate}</TableColumn> */}
      <TableColumn style={{ marginLeft: 20 }} maxWidth={200}>
        {subtotal}
      </TableColumn>
      <TableColumn maxWidth={5}>
        {!!currentItemTypePackage && (
          <div className="action justify-content-center align-items-center">
            <a onClick={() => props.onOpen && props.onOpen()} className="float-right" style={{ color: '#495057' }}>
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
          </div>
        )}
      </TableColumn>
    </div>
  );
});

export interface SectionItemProps {
  form: PriceSectionForm;
  secIndex: number;
  lineItemForms: LineItemForm[];
  primaryColor: string | undefined;
  total: number;
  canSign: boolean;
  labourTypes: LabourTypeResponse[];
  isLaborActive: boolean;
  editable?: boolean;
  canCheckProducts?: boolean;
}

export const SectionItem = memo((props: SectionItemProps) => {
  const {
    form,
    secIndex,
    lineItemForms,
    primaryColor,
    total,
    canSign,
    isLaborActive,
    labourTypes = [],
    editable = true,
    canCheckProducts = true,
  } = props;
  const formValue = useNonNilObservable(form.value$);

  const filteredItems = useMemo(
    () => lineItemForms.filter(item => R.toNumber(item.controls.sectionId.value) == R.toNumber(formValue.id)),
    [formValue.id, lineItemForms],
  );

  const isDefault = useMemo(() => formValue.title == 'Default Section', [formValue.title]);

  return (
    <div className="template-section border-bottom pb-2 mb-2">
      {/* {!formValue.hide && ( */}
      {!isDefault && (
        <CardTitle className="d-flex align-items-center justify-content-between">
          <div className="row py-2 ml-1">
            <span className="title mr-3 mt-2 font-size-24">{formValue.title}</span>
          </div>
        </CardTitle>
      )}
      {/* )} */}
      {!!filteredItems.length && <PricingHeader isLaborActive={isLaborActive} primaryColor={primaryColor} editable={editable} />}
      {filteredItems.map((item, index) => {
        const isItPackage = item.value.packageItems.length > 0 ? true : false;
        return (
          <>
            {isItPackage ? (
              <ColapsableLineItem
                key={item.uid}
                isLaborActive={isLaborActive}
                form={item}
                index={index}
                canSign={canSign}
                labourTypes={labourTypes}
                editable={editable}
                canCheckProducts={canCheckProducts}
              />
            ) : (
              <LineItem
                key={item.uid}
                isLaborActive={isLaborActive}
                form={item}
                index={index}
                canSign={canSign}
                labourTypes={labourTypes}
                isPackage={item.value.packageItems.length > 0 ? true : false}
                editable={editable}
                canCheckProducts={canCheckProducts}
              />
            )}
          </>
        );
      })}
      {/* <PricingFooter total={total} primaryColor={primaryColor} /> */}
    </div>
  );
});

export interface ColapsableLineItemProps {
  isLaborActive: boolean;
  form: LineItemForm;
  index: number;
  canSign: boolean;
  labourTypes: LabourTypeResponse[];
  editable: boolean;
  canCheckProducts?: boolean;
}

export const ColapsableLineItem = memo((props: ColapsableLineItemProps) => {
  const { isLaborActive, form, index, canSign, labourTypes, editable, canCheckProducts = true } = props;
  const [isOpen, setOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const formValue = useNonNilObservable(form.value$);

  return (
    <>
      <LineItem
        isLaborActive={isLaborActive}
        form={form}
        index={index}
        canSign={canSign}
        labourTypes={labourTypes}
        isPackage={true}
        editable={editable}
        onOpen={() => setOpen(!isOpen)}
        isOpen={isOpen}
        canCheckProducts={canCheckProducts}
      />

      <Collapse isOpen={isOpen}>
        {form.controls.packageItems.items.map((packageItem, pacInd) => {
          return (
            <div className="ml-5" key={packageItem.uid}>
              <LineItem
                isLaborActive={isLaborActive}
                key={packageItem.uid}
                form={packageItem}
                index={pacInd}
                canSign={canSign}
                labourTypes={labourTypes}
                isPackage={form.value.packageItems.length > 0 ? true : false}
                editable={editable}
                canCheckProducts={canCheckProducts}
              />
            </div>
          );
        })}
      </Collapse>
    </>
  );
});
